/**
 * IRI <-> prefixed-name compaction. Lifted from ontology-explorer's `rdf/prefixes.ts`
 * (the house pattern for this monorepo) and extended with the Wikidata namespaces
 * the SPARQL backend needs. Longest-namespace-first matching means `wdt:`
 * (`.../prop/direct/`) wins over `p:` (`.../prop/`) correctly.
 */

export const WELL_KNOWN_PREFIXES: Record<string, string> = {
  rdf: "http://www.w3.org/1999/02/22-rdf-syntax-ns#",
  rdfs: "http://www.w3.org/2000/01/rdf-schema#",
  owl: "http://www.w3.org/2002/07/owl#",
  xsd: "http://www.w3.org/2001/XMLSchema#",
  skos: "http://www.w3.org/2004/02/skos/core#",
  // Wikidata
  wd: "http://www.wikidata.org/entity/",
  wdt: "http://www.wikidata.org/prop/direct/",
  p: "http://www.wikidata.org/prop/",
  ps: "http://www.wikidata.org/prop/statement/",
  pq: "http://www.wikidata.org/prop/qualifier/",
  wikibase: "http://wikiba.se/ontology#",
  bd: "http://www.bigdata.com/rdf#",
};

export class PrefixTable {
  private byNamespaceDesc: Array<[string, string]> = [];
  private byPrefix = new Map<string, string>();

  constructor(seed: Record<string, string> = WELL_KNOWN_PREFIXES) {
    for (const [prefix, namespaceIri] of Object.entries(seed)) {
      this.register(prefix, namespaceIri);
    }
  }

  register(prefix: string, namespaceIri: string): void {
    if (prefix === "" || this.byPrefix.has(prefix)) {
      if (this.byPrefix.has(prefix)) return;
    }
    this.byPrefix.set(prefix, namespaceIri);
    this.byNamespaceDesc.push([namespaceIri, prefix]);
    this.byNamespaceDesc.sort((a, b) => b[0].length - a[0].length);
  }

  /** Compact a full IRI to `prefix:localName`, or return it unchanged if nothing matches. */
  compact(iri: string): string {
    for (const [namespaceIri, prefix] of this.byNamespaceDesc) {
      if (iri.startsWith(namespaceIri) && iri.length > namespaceIri.length) {
        return `${prefix}:${iri.slice(namespaceIri.length)}`;
      }
    }
    return iri;
  }

  expand(prefixedName: string): string | null {
    const colonIndex = prefixedName.indexOf(":");
    if (colonIndex === -1) return null;
    const prefix = prefixedName.slice(0, colonIndex);
    const namespaceIri = this.byPrefix.get(prefix);
    if (!namespaceIri) return null;
    return namespaceIri + prefixedName.slice(colonIndex + 1);
  }

  /** `PREFIX p: <ns>` header lines for the given prefixes (defaults to all known). */
  header(prefixes?: string[]): string {
    const names = prefixes ?? [...this.byPrefix.keys()];
    return names
      .map((p) => {
        const ns = this.byPrefix.get(p);
        return ns ? `PREFIX ${p}: <${ns}>` : "";
      })
      .filter(Boolean)
      .join("\n");
  }
}
